'use client';

import type { ReactNode } from 'react';

import useMap from '@/hooks/useMap';
import Icon from '@/components/shared/Icons';
import Button from '@/components/shared/button/Button';
import { useCallback, useMemo } from 'react';
import { prepareUrl } from '@/utils/urlHelper';
import { usePathname, useSearchParams } from 'next/navigation';
import { queryStringConstants, VIEW } from '@/constants/queryConstants';
import { useRouter } from '@/lib/router-events/patch-router/router';
import { MAP_SIZE } from '@/constants/mapConstants';

export default function LeftContentContainer({
  mapViewText,
  children,
  totalVenues
}: {
  mapViewText: string;
  children?: ReactNode;
  totalVenues: number;
}): JSX.Element {
  const { mapView } = useMap();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const view = useMemo(() => searchParams?.get(queryStringConstants.VIEW), [searchParams]);

  const styles = useMemo((): { container: string; button: string } => {
    const expanded = `flex w-full max-w-screen-lg flex-col gap-y-6 p-6 xl:px-0 xl:w-3/5 ${
      view === VIEW.MAP ? 'hidden xl:block' : ''
    }`;
    const collapsed = `flex w-full max-w-screen-lg flex-col gap-y-6 p-6 xl:px-0 xl:w-3/4 ${
      view === VIEW.MAP ? 'hidden xl:block' : ''
    }`;
    const buttonStyle = `fixed bottom-9 left-[calc(50%_-_4.5rem)] z-20 ${
      view !== VIEW.MAP ? 'block' : 'hidden'
    } lg:hidden`;
    return { container: mapView === MAP_SIZE.MEDIUM ? expanded : collapsed, button: buttonStyle };
  }, [view, mapView]);

  const onClick = useCallback(() => {
    router.push(prepareUrl(pathname, searchParams, { key: queryStringConstants.VIEW, value: VIEW.MAP }));
  }, [pathname, router, searchParams]);

  return (
    <div className={styles.container} data-cvent-id="left-content-container">
      {totalVenues > 0 && (
        <div className={styles.button}>
          <Button
            aria-label={mapViewText}
            className="rounded-full"
            appearance="filled"
            icon={<Icon id="map-view" className="h-4 w-4 fill-background" />}
            text={mapViewText}
            onClick={onClick}
          />
        </div>
      )}
      {children}
    </div>
  );
}
